import { SMS, WEB_PUSH } from 'constants/messageMediums';
import { useMemo } from 'react';
import { useCampaignAlertJourneyContext } from '../BaseSendEditor.context';
import { useCampaign, useFeatureFlags } from '../BaseSendEditor.hooks';

import { BaseSendEditorProps, CurrentProject } from '../BaseSendEditor.types';
import SectionTitle from '../SectionTitle';
import { CampaignAlertCheckbox } from './CampaignAlertsCheckbox';

import DisplayPriority from './DisplayPriority';
import { IgnoreFrequencyCapCheckbox, useIgnoreCapAvailability } from './IgnoreFrequencyCapCheckbox';
import QuietHoursCheckbox from './QuietHoursCheckbox';
import RateLimitingCheckbox from './RateLimitingCheckbox';
import STOCheckbox from './STOCheckbox';
import SelectiveTargeting from './SelectiveTargeting';

export interface DeliverySectionProps extends BaseSendEditorProps {
  /** The title shown in section header */
  title?: string;
  project: CurrentProject;
  banner?: BaseSendEditorProps['deliveryBanner'];
  isDisabled?: boolean;
  templateId: number | undefined;
}

export const DeliverySection = ({
  title = 'Delivery',
  banner,
  isDisabled = false,
  templateId,
  ...props
}: DeliverySectionProps) => {
  const {
    messageMedium,
    project,
    shouldShowDeliverySectionHeader: showSectionHeader = false,
    shouldShowDisplayPriority: showDisplayPriority = false,
    shouldShowQuietHours: showQuietHours = false,
    shouldShowRateLimit: showRateLimit = false,
    shouldShowSTO: showSTO = false,
  } = props;
  const { campaign, updateCampaign, errors: campaignErrors } = useCampaign();
  const { isLoadingAlerts } = useCampaignAlertJourneyContext();
  const {
    quietHoursIsEnabled,
    campaignRateLimitingIsEnabled,
    isCampaignPerformanceAlertsEnabled,
    isJourneysCampaignPerformanceAlertsEnabled,
  } = useFeatureFlags();
  const isHeaderShown = useMemo(() => {
    if (!showSectionHeader) return false;
    if (messageMedium === SMS || messageMedium === WEB_PUSH) return !!quietHoursIsEnabled;
    return true;
  }, [messageMedium, quietHoursIsEnabled, showSectionHeader]);
  const { STOQHOverlap: stoQhOverlapWarning, STOQHAlertsOverlap: stoQhAlertsOverlapWarning } =
    campaignErrors || {};

  const { isIgnoreCapVisible, isIgnoreCapEditable } = useIgnoreCapAvailability(templateId);

  return (
    <>
      {isHeaderShown && <SectionTitle title={title} banner={banner} />}
      {quietHoursIsEnabled && showQuietHours && (
        <QuietHoursCheckbox
          {...props}
          quietHours={campaign?.customQuietHours || null}
          onChange={(customQuietHours) => updateCampaign({ customQuietHours })}
          hasError={campaignErrors.QHSameStartAndEnd?.hasError !== false}
          validationText={campaignErrors.QHSameStartAndEnd?.message}
          isDisabled={isDisabled}
        />
      )}
      {showSTO && (
        <STOCheckbox
          {...props}
          campaign={campaign}
          onChange={updateCampaign}
          timeZone={project?.timeZone}
          warningText={stoQhOverlapWarning?.message || ''}
          isDisabled={isDisabled}
        />
      )}
      {campaignRateLimitingIsEnabled && showRateLimit && (
        <RateLimitingCheckbox
          {...props}
          rateLimit={campaign?.customRateLimit}
          onChange={(customRateLimit) => updateCampaign({ customRateLimit })}
          isDisabled={isDisabled}
        />
      )}
      {isCampaignPerformanceAlertsEnabled &&
        isJourneysCampaignPerformanceAlertsEnabled &&
        !isLoadingAlerts && (
          <CampaignAlertCheckbox
            {...props}
            campaign={campaign}
            warningText={stoQhAlertsOverlapWarning?.message}
            isDisabled={isDisabled}
            onChange={(campaignAlertsEnabled) => updateCampaign({ campaignAlertsEnabled })}
          />
        )}
      {isIgnoreCapVisible && (
        <IgnoreFrequencyCapCheckbox
          {...props}
          ignoreFrequencyCap={!!campaign?.ignoreFrequencyCap}
          onChange={(ignoreFrequencyCap) => updateCampaign({ ignoreFrequencyCap })}
          isDisabled={isDisabled || !isIgnoreCapEditable}
          tooltipContent={
            !isIgnoreCapEditable
              ? 'There are no frequency caps set for this channel or message type'
              : undefined
          }
        />
      )}
      <SelectiveTargeting
        {...props}
        onChange={(mobileAppIds) => updateCampaign({ mobileAppIds })}
        isDisabled={isDisabled}
      />
      {showDisplayPriority && (
        <DisplayPriority
          {...props}
          priorityLevel={campaign?.priorityLevel}
          onChange={(priorityLevel) => updateCampaign({ priorityLevel })}
          isDisabled={isDisabled}
        />
      )}
    </>
  );
};

export default DeliverySection;
